import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { formatFileSize, INTAKE_STORAGE_LIMIT_LABEL, type OrganizationLimits } from "@ravnar/api-contract";

import { organizationLimitsQueryOptions } from "../../api/settings";
import { PageHeader } from "../../components/shell/app-shell";
import { Card, CardHeader } from "../../components/ui/card";
import { Meter } from "../../components/ui/meter";
import { isLimitReached, limitMeter } from "../../domain/limits";
import { formatInteger, formatMonthDay } from "../../lib/format";

export const Route = createFileRoute("/settings/limits")({
	// Prefetch through the Query cache so the page can use useSuspenseQuery without a loading flash.
	loader: ({ context }) => context.queryClient.query({ ...organizationLimitsQueryOptions(), staleTime: "static" }),
	head: () => ({ meta: [{ title: "Limits · Ravnar" }] }),
	staticData: { crumbs: ["Settings", "Limits"] },
	component: LimitsPage,
});

/** "1 document waits" / "3 documents wait". */
function waitingText(count: number): string {
	return count === 1 ? "1 document waits to be read." : `${String(count)} documents wait to be read.`;
}

/** A figure as a limit card leads with it: large, over "of" what it may be at most. */
function Figure({ value, of }: { readonly value: string; readonly of: string }) {
	return (
		<p className="text-body text-ink-2">
			<span className="text-figure font-semibold tracking-title text-ink tabular-nums">{value}</span> of {of}
		</p>
	);
}

/** Every member sees the organization's limits and where it stands; nothing here can be changed. */
function LimitsPage() {
	const { data: limits } = useSuspenseQuery(organizationLimitsQueryOptions());
	return (
		<>
			<PageHeader
				title="Limits"
				description="What the organization may use of Ravnar, and how much of it it has used."
			/>
			<div className="flex flex-col gap-3.5">
				<StorageCard storage={limits.storage} />
				<AiTokensCard aiTokens={limits.aiTokens} waitingDocuments={limits.waitingDocuments} />
			</div>
		</>
	);
}

function StorageCard({ storage: { used, limit } }: { readonly storage: OrganizationLimits["storage"] }) {
	const full = isLimitReached(used, limit);
	// All of it by its own name, "500 MB", rather than as a size, "500.0 MB".
	const left = used === 0 ? INTAKE_STORAGE_LIMIT_LABEL : formatFileSize(limit - used);
	return (
		<Card>
			<CardHeader title="Storage" />
			<div className="flex flex-col gap-3 p-4">
				<Figure value={formatFileSize(used)} of={INTAKE_STORAGE_LIMIT_LABEL} />
				<Meter {...limitMeter(used, limit)} />
				<p className={full ? "text-caption text-alert" : "text-caption text-ink-2"}>
					{full ? "Full: Intake takes no more documents." : `${left} left for Intake's documents.`}
				</p>
				<p className="text-caption text-ink-3">
					Every document counts once, at the size it was uploaded with. Nothing deletes a document, so what it
					takes stays taken.
				</p>
			</div>
		</Card>
	);
}

function AiTokensCard({
	aiTokens: { used, limit, resetsAt },
	waitingDocuments,
}: {
	readonly aiTokens: OrganizationLimits["aiTokens"];
	readonly waitingDocuments: number;
}) {
	const spent = isLimitReached(used, limit);
	const resetDay = formatMonthDay(resetsAt.slice(0, 10));
	return (
		<Card>
			<CardHeader title="AI tokens this month" />
			<div className="flex flex-col gap-3 p-4">
				<Figure value={formatInteger(used)} of={formatInteger(limit)} />
				<Meter {...limitMeter(used, limit)} />
				<p className={spent ? "text-caption text-warn" : "text-caption text-ink-2"}>
					{spent
						? `This month's tokens are used up. Documents are still uploaded and kept, and are read from ${resetDay}.`
						: `${formatInteger(limit - used)} left until ${resetDay}.`}
					{waitingDocuments > 0 ? ` ${waitingText(waitingDocuments)}` : ""}
				</p>
				<p className="text-caption text-ink-3">
					The count starts again at midnight UTC on the first of each month. It counts every answer the
					Directory agent, the Document linker and Document Filing get from the model, what they send it and
					what it writes; Bedrock's reading of a document is not counted.
				</p>
			</div>
		</Card>
	);
}
